import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSuggestedActivities } from "@/components/dashboard/suggested-activities/useSuggestedActivities";
import { ActivityItem } from "@/components/dashboard/suggested-activities/ActivityItem";
import { Skeleton } from "@/components/ui/skeleton";
import { Lightbulb, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const SuggestedActivitiesWidget = () => {
  const navigate = useNavigate();
  
  const {
    displayedActivities,
    isLoading,
    startActivity,
    completeActivity,
  } = useSuggestedActivities();

  const isMutationPending = startActivity?.isPending || completeActivity?.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">AI Suggested Activities</CardTitle>
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start space-x-3">
                <Skeleton className="h-4 w-4 mt-1" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const pendingActivities = displayedActivities?.filter(a => a.status !== 'completed') || [];
  const highPriorityActivities = pendingActivities.filter(a => a.priority === 'high');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base font-medium">AI Suggested Activities</CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')} // Could navigate to a dedicated activities page
            className="h-8 px-2 text-xs"
          >
            View All
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        {!pendingActivities || pendingActivities.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No suggestions at the moment</p>
            <p className="text-xs">Your AI assistant will suggest activities as you work</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Show high priority activities first */}
            {highPriorityActivities.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="destructive" className="text-xs">High Priority</Badge>
                  <span className="text-xs text-muted-foreground">{highPriorityActivities.length} urgent</span>
                </div>
                {highPriorityActivities.slice(0, 2).map((activity) => (
                  <ActivityItem
                    key={activity.id}
                    activity={activity}
                    onStart={(act) => startActivity?.mutate(act)}
                    onComplete={(act) => completeActivity?.mutate(act)}
                    isMutationPending={isMutationPending}
                  />
                ))}
              </div>
            )}

            {/* Show other activities */}
            {pendingActivities.filter(a => a.priority !== 'high').slice(0, 3).map((activity) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                onStart={(act) => startActivity?.mutate(act)}
                onComplete={(act) => completeActivity?.mutate(act)}
                isMutationPending={isMutationPending}
              />
            ))}

            {pendingActivities.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')} // Navigate to full activities view
                className="w-full mt-2 text-xs"
              >
                View All Activities ({pendingActivities.length})
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};